export function normalizeClientDocuments(documents) {
  if (!Array.isArray(documents)) return [];
  return documents.filter((doc) => doc && (doc.url || doc.file_path || doc.name));
}

export function splitClientDocuments(documents = []) {
  const list = normalizeClientDocuments(documents);
  const finance = [];
  const general = [];
  const providerContracts = [];

  list.forEach((doc) => {
    const type = String(doc?.type || "").trim().toLowerCase();
    if (type === "provider_contract") {
      providerContracts.push(doc);
    } else if (type === "finance" || type === "financial") {
      finance.push(doc);
    } else {
      general.push(doc);
    }
  });

  return { general, finance, providerContracts };
}

export function getDocumentLabel(document = {}) {
  return (
    document.name ||
    document.document_name ||
    document.original_name ||
    document.file_name ||
    "Document"
  );
}

export function getDocumentUrl(document = {}) {
  return document.url || document.file_url || document.file_path || "";
}

function getExtension(value = "") {
  const clean = String(value).split("?")[0].split("#")[0];
  const parts = clean.split(".");
  if (parts.length < 2) return "";
  return parts.pop()?.toLowerCase() || "";
}

export function getDocumentPreviewKind(document = {}) {
  const url = getDocumentUrl(document);
  const label = getDocumentLabel(document);
  const ext = getExtension(label) || getExtension(url);

  if (ext === "pdf") return "pdf";
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext)) {
    return "image";
  }
  return "other";
}

export function countClientDocuments(documents = []) {
  return normalizeClientDocuments(documents).length;
}

export function resolveCommissionAppType(category) {
  const raw = String(category || "education").toLowerCase();
  if (raw === "education" || raw === "edu") return "edu";
  if (raw === "migration" || raw === "mig") return "mig";
  return raw || "edu";
}

export function mapClientDocumentForModal(doc = {}) {
  const typeRaw = String(doc?.type || "").trim().toLowerCase();
  const type =
    typeRaw === "finance" || typeRaw === "financial"
      ? "finance"
      : typeRaw || "client_document";

  return {
    ...doc,
    id: doc.id,
    name: doc.document_name || doc.name,
    document_name: doc.document_name || doc.name,
    type,
    app_type: doc.app_type,
    url: doc.url || doc.file_url || doc.file_path || null,
  };
}

function mapProviderContractForModal(contract = {}) {
  const url =
    contract.url ||
    (contract.document_path
      ? String(contract.document_path).startsWith("http")
        ? contract.document_path
        : null
      : null);

  return {
    id: contract.id,
    name: contract.document_name || contract.name || "Provider contract",
    document_name: contract.document_name,
    type: "provider_contract",
    app_type: "provider",
    url,
    provider_id: contract.provider_id,
  };
}

/**
 * Client documents + provider contracts (same merge as commission list rows).
 */
export async function fetchCommissionApplicationDocuments({
  clientId,
  providerId,
  apiUrl,
}) {
  if (!clientId || !apiUrl) {
    return [];
  }

  const clientDocsPromise = apiUrl
    .get(`get-client-documents/${clientId}`)
    .then((response) => {
      if (!response?.data?.status) return [];
      const rows = Array.isArray(response.data.data) ? response.data.data : [];
      return rows.map(mapClientDocumentForModal);
    })
    .catch(() => []);

  let providerDocs = [];
  if (providerId) {
    providerDocs = await apiUrl
      .get(`get-provider-contracts/${providerId}`)
      .then((response) => {
        if (!response?.data?.status) return [];
        const rows = Array.isArray(response.data.data) ? response.data.data : [];
        return rows.map(mapProviderContractForModal);
      })
      .catch(() => []);
  }

  const clientDocs = await clientDocsPromise;
  return [...clientDocs, ...providerDocs];
}